/*
 * Copyright 2018 Teralytics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Layer, picking, project32 } from '@deck.gl/core';
import { TRIANGLES, UNSIGNED_BYTE, DOUBLE } from '@luma.gl/constants';
import { Geometry, Model } from '@luma.gl/core';
import FragmentShader from './FlowLinesLayerFragment.glsl';
import VertexShader from './FlowLinesLayerVertex.glsl';
var DEFAULT_COLOR = [0, 132, 193, 255];
var INNER_SIDE_OUTLINE_THICKNESS = 1;
var FlowLinesLayer = /** @class */ (function (_super) {
    __extends(FlowLinesLayer, _super);
    function FlowLinesLayer(props) {
        return _super.call(this, props) || this;
    }
    FlowLinesLayer.prototype.getShaders = function () {
        return _super.prototype.getShaders.call(this, {
            vs: VertexShader,
            fs: FragmentShader,
            modules: [project32, picking],
            shaderCache: this.context.shaderCache,
        });
    };
    FlowLinesLayer.prototype.initializeState = function () {
        var attributeManager = this.state.attributeManager;
        attributeManager.addInstanced({
            instanceSourcePositions: {
                accessor: 'getSourcePosition',
                size: 3,
                transition: false,
                type: DOUBLE,
            },
            instanceTargetPositions: {
                accessor: 'getTargetPosition',
                size: 3,
                transition: false,
                type: DOUBLE,
            },
            instanceThickness: {
                accessor: 'getThickness',
                size: 1,
                transition: false,
            },
            instanceEndpointOffsets: {
                accessor: 'getEndpointOffsets',
                size: 2,
                transition: false,
            },
            instanceColors: {
                accessor: 'getColor',
                size: 4,
                type: UNSIGNED_BYTE,
                transition: false,
            },
            instancePickable: {
                accessor: 'getPickable',
                size: 1,
                transition: false,
            },
        });
    };
    FlowLinesLayer.prototype.updateState = function (_a) {
        var props = _a.props, oldProps = _a.oldProps, changeFlags = _a.changeFlags;
        _super.prototype.updateState.call(this, { props: props, oldProps: oldProps, changeFlags: changeFlags });
        if (changeFlags.extensionsChanged) {
            var gl = this.context.gl;
            if (this.state.model) {
                this.state.model.delete();
            }
            this.setState({ model: this._getModel(gl) });
            this.getAttributeManager().invalidateAll();
        }
    };
    FlowLinesLayer.prototype.draw = function (_a) {
        var uniforms = _a.uniforms;
        var gl = this.context.gl;
        var _b = this.props, outlineColor = _b.outlineColor, thicknessUnit = _b.thicknessUnit;
        gl.lineWidth(1);
        this.state.model
            .setUniforms(__assign(__assign({}, uniforms), { outlineColor: outlineColor.map(function (x) { return x / 255; }), thicknessUnit: thicknessUnit * 2.0, gap: 0.5 }))
            .draw();
    };
    FlowLinesLayer.prototype._getModel = function (gl) {
        var positions = [];
        var pixelOffsets = [];
        var _a = this.props, drawOutline = _a.drawOutline, outlineThickness = _a.outlineThickness;
        if (drawOutline) {
            // source_target_mix, perpendicular_offset_in_thickness_units, direction_of_travel_offset_in_thickness_units
            // prettier-ignore
            positions = positions.concat([
                // Outline
                0, 0, 0,
                0, 1, 0,
                1, 0, 0,
                0, 1, 0,
                1, 0, -3,
                1, 1, -3,
                1, 0, 0,
                1, 2, -3,
                1, 0, -3,
            ]);
            var tout = outlineThickness;
            var tin = INNER_SIDE_OUTLINE_THICKNESS; // the outline shouldn't cover the opposite arrow
            // perpendicular_offset_in_pixels, direction_of_travel_offset_in_pixels, fill_outline_color_mix
            // prettier-ignore
            pixelOffsets = pixelOffsets.concat([
                // Outline
                -tin, -tout, 1,
                tout, -tout, 1,
                -tin, tout, 1,
                tout, -tout, 1,
                -tin, 0, 1,
                tout, 0, 1,
                -tin, 3 * tout, 1,
                2 * tout, -tout, 1,
                -tin, -tout, 1,
            ]);
        }
        // prettier-ignore
        positions = positions.concat([
            // Fill
            0, 0, 0,
            0, 1, 0,
            1, 0, 0,
            0, 1, 0,
            1, 0, -3,
            1, 1, -3,
            1, 0, 0,
            1, 2, -3,
            1, 0, -3,
        ]);
        // prettier-ignore
        pixelOffsets = pixelOffsets.concat([
            // Fill
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
            0, 0, 0,
        ]);
        return new Model(gl, __assign(__assign({ id: this.props.id }, this.getShaders()), { geometry: new Geometry({
                drawType: TRIANGLES,
                attributes: {
                    positions: new Float32Array(positions),
                    normals: new Float32Array(pixelOffsets),
                },
            }), isInstanced: true, shaderCache: this.context.shaderCache }));
    };
    FlowLinesLayer.layerName = 'FlowLinesLayer';
    FlowLinesLayer.defaultProps = {
        getSourcePosition: { type: 'accessor', value: function (d) { return d.sourcePosition; } },
        getTargetPosition: { type: 'accessor', value: function (d) { return d.targetPosition; } },
        getColor: { type: 'accessor', value: DEFAULT_COLOR },
        getThickness: { type: 'accessor', value: function (d) { return d.thickness; } },
        getPickable: { type: 'accessor', value: function (d) { return 1.0; } },
        drawOutline: true,
        thicknessUnit: 10,
        outlineThickness: 1,
        outlineColor: [255, 255, 255, 255],
        parameters: {
            depthTest: false,
        },
    };
    return FlowLinesLayer;
}(Layer));
export default FlowLinesLayer;
//# sourceMappingURL=FlowLinesLayer.js.map